import React from "react";
import { PageHeader } from "../../field-service/components/PageHeader/PageHeader";
import { useFinanceConfig } from "../../field-service/context/FinanceConfigContext";

// ─── Types ────────────────────────────────────────────────────────────────────

type AssetTab = "Overview" | "Activity Log" | "History" | "Documentation" | "Sub Assets" | "PM Events";

interface AssetPart {
  id: string;
  name: string;
  articleNumber: string;
}

interface AssetProcedure {
  id: string;
  name: string;
  fields: number;
  createdDate: string;
}

export interface AssetDetailPageProps {
  assetId: string;
  assetName: string;
  onBack?: () => void;
  onCreateWorkOrder?: () => void;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────


const MOCK_PARTS: AssetPart[] = [
  { id: "p1", name: "Electrical Part",  articleNumber: "00011JP" },
  { id: "p2", name: "Mechanical Part",  articleNumber: "00012JP" },
  { id: "p3", name: "Hydraulic Part",   articleNumber: "00013JP" },
  { id: "p4", name: "Engine Part",      articleNumber: "00013JP" },
];

const MOCK_PROCEDURES: AssetProcedure[] = [
  { id: "pr1", name: "Condition Assessment Checklist", fields: 16, createdDate: "20-10-2025" },
  { id: "pr2", name: "Electrical Unit Checklist",      fields: 10, createdDate: "01-01-2024" },
  { id: "pr3", name: "Preventive Maintenance Checklist", fields: 20, createdDate: "01-01-2024" },
];

// ─── Icons ────────────────────────────────────────────────────────────────────

function QRIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="1" y="1" width="6.5" height="6.5" rx="1" stroke="#374151" strokeWidth="1.3" />
      <rect x="10.5" y="1" width="6.5" height="6.5" rx="1" stroke="#374151" strokeWidth="1.3" />
      <rect x="1" y="10.5" width="6.5" height="6.5" rx="1" stroke="#374151" strokeWidth="1.3" />
      <rect x="2.5" y="2.5" width="3" height="3" fill="#374151" />
      <rect x="12" y="2.5" width="3" height="3" fill="#374151" />
      <rect x="2.5" y="12" width="3" height="3" fill="#374151" />
      <path d="M10.5 10.5h2.5M14.5 10.5H17M10.5 13h1.5M13.5 13h1.5M16 13h1M10.5 15.5h1.5M13 15.5h1.5M15.5 15.5h1.5" stroke="#374151" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function ChevronUpIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 10l4-4 4 4" />
    </svg>
  );
}

function ChevronDownIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 2.5l3 3.5-3 3.5" />
    </svg>
  );
}

function PlusIcon({ color = "#0517f8" }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <path d="M7 2.5v9M2.5 7h9" />
    </svg>
  );
}

function MoreDotsIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill={color}>
      <circle cx="3.5" cy="8" r="1.3" />
      <circle cx="8" cy="8" r="1.3" />
      <circle cx="12.5" cy="8" r="1.3" />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#D1D5DB" strokeWidth="1.4" strokeLinecap="round">
      <circle cx="9" cy="9" r="7.5" />
      <path d="M6 9h6" />
    </svg>
  );
}

function PartIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#0517f8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1.5L2 4.5v7l6 3 6-3v-7L8 1.5z" />
      <path d="M2 4.5l6 3 6-3M8 7.5v7" />
    </svg>
  );
}

function ProcedureIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#0517f8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="1.5" width="12" height="13" rx="1.5" />
      <path d="M5 5h6M5 8h6M5 11h3" />
      <path d="M9 10.5l1.5 1.5 2.5-2.5" />
    </svg>
  );
}

function PartRowIcon() {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: 6,
      background: "#EEF0FF", border: "1px solid #E0E3FF",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#0517f8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 1L1.5 4v6L7 13l5.5-3V4L7 1z" />
        <path d="M1.5 4l5.5 3 5.5-3M7 7v6" />
      </svg>
    </div>
  );
}

function ProcedureRowIcon() {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: 6,
      background: "#EEF0FF", border: "1px solid #E0E3FF",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#0517f8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1.5" y="1" width="11" height="12" rx="1.5" />
        <path d="M4 4.5h6M4 7h6M4 9.5h3" />
      </svg>
    </div>
  );
}

function CalendarIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="2" width="12" height="11" rx="1.5" />
      <path d="M1 6h12M4.5 1v2M9.5 1v2" />
    </svg>
  );
}

function UserIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="4.5" r="2.5" />
      <path d="M1.5 13c0-3.038 2.462-5 5.5-5s5.5 1.962 5.5 5" />
    </svg>
  );
}

function SNIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="12" height="8" rx="1.5" />
      <path d="M4 7h6M4 9h3" />
      <circle cx="10" cy="9" r="0.5" fill={color} stroke="none" />
    </svg>
  );
}

function AssetTypeIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 1L1.5 4v6L7 13l5.5-3V4L7 1z" />
      <path d="M1.5 4l5.5 3 5.5-3M7 7v6" />
    </svg>
  );
}

function TeamIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="4.5" r="2" />
      <path d="M1 13c0-2.761 1.791-4.5 4-4.5s4 1.739 4 4.5" />
      <path d="M9 6c.5 0 1 .2 1.4.55M11.5 13c0-2.761-1-4.5-2.5-4.5" />
    </svg>
  );
}

function BuildingIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="3" width="8" height="10" rx="1" />
      <path d="M9.5 5.5H12a.5.5 0 01.5.5v7" />
      <path d="M4 6h2M4 8.5h2M4 11h2" />
    </svg>
  );
}

function AssetThumb({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, display: "block" }}>
      <path d="M0 8C0 3.58172 3.58172 0 8 0H40C44.4183 0 48 3.58172 48 8V40C48 44.4183 44.4183 48 40 48H8C3.58172 48 0 44.4183 0 40V8Z" fill="#E6E8FE"/>
      <path d="M23.4664 14.7822L20.775 17.4736C20.4821 17.7665 20.4821 18.2413 20.775 18.5342L23.4644 21.2237C23.7573 21.5166 24.2322 21.5166 24.525 21.2237L27.2165 18.5322C27.5094 18.2394 27.5094 17.7645 27.2165 17.4716L24.527 14.7822C24.2341 14.4893 23.7593 14.4893 23.4664 14.7822Z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M29.4672 20.7822L26.7758 23.4736C26.4829 23.7665 26.4829 24.2413 26.7758 24.5342L29.4653 27.2237C29.7581 27.5166 30.233 27.5166 30.5259 27.2237L33.2173 24.5322C33.5102 24.2394 33.5102 23.7645 33.2173 23.4716L30.5279 20.7822C30.235 20.4893 29.7601 20.4893 29.4672 20.7822Z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M17.4664 20.7743L14.775 23.4658C14.4821 23.7587 14.4821 24.2335 14.775 24.5264L17.4644 27.2159C17.7573 27.5088 18.2322 27.5088 18.5251 27.2159L21.2165 24.5244C21.5094 24.2315 21.5094 23.7567 21.2165 23.4638L18.527 20.7743C18.2342 20.4814 17.7593 20.4814 17.4664 20.7743Z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M23.4672 26.7743L20.7758 29.4658C20.4829 29.7587 20.4829 30.2335 20.7758 30.5264L23.4653 33.2159C23.7581 33.5088 24.233 33.5088 24.5259 33.2159L27.2173 30.5244C27.5102 30.2315 27.5102 29.7567 27.2173 29.4638L24.5279 26.7743C24.235 26.4814 23.7601 26.4814 23.4672 26.7743Z" stroke="#0517F8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function AssetIconSmall({ color = "#6974fb" }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 1L1.5 4v6L7 13l5.5-3V4L7 1z" />
      <path d="M1.5 4l5.5 3 5.5-3M7 7v6" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M5.5 1.5C6 4.5 6 4.5 9 5.5C6 6.5 6 6.5 5.5 9.5C5 6.5 5 6.5 2 5.5C5 4.5 5 4.5 5.5 1.5Z" fill="#172b4d" />
      <path d="M10.5 1C10.8 2.4 10.8 2.4 12 2.7C10.8 3 10.8 3 10.5 4.5C10.2 3 10.2 3 9 2.7C10.2 2.4 10.2 2.4 10.5 1Z" fill="#172b4d" />
    </svg>
  );
}

function SingleSelectIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round">
      <circle cx="7" cy="7" r="6" />
      <circle cx="7" cy="7" r="2.5" fill="#748094" stroke="none" />
    </svg>
  );
}

function MultiSelectIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="1.5" width="5" height="5" rx="1" />
      <rect x="7.5" y="1.5" width="5" height="5" rx="1" />
      <rect x="1.5" y="7.5" width="5" height="5" rx="1" />
      <rect x="7.5" y="7.5" width="5" height="5" rx="1" />
    </svg>
  );
}

function TextIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round">
      <path d="M2 3.5h10M7 3.5v7M4 10.5h6" />
    </svg>
  );
}

function NumberIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round">
      <path d="M2.5 4.5h9M2.5 9.5h9M5 2l-1.5 10M10.5 2L9 12" />
    </svg>
  );
}

function DateFieldIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="2" width="12" height="11" rx="1.5" />
      <path d="M1 6h12M4.5 1v2M9.5 1v2" />
    </svg>
  );
}

function DescriptionIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="1.5" width="12" height="13" rx="1.5" />
      <path d="M5 5h6M5 8h6M5 11h4" />
    </svg>
  );
}

function Avatar({ initials, color }: { initials: string; color: string }) {
  return (
    <div style={{
      width: 20, height: 20, borderRadius: "50%", background: color,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: "white", fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px" }}>
        {initials}
      </span>
    </div>
  );
}

// ─── Sidebar Detail Row ───────────────────────────────────────────────────────

function SidebarRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", minHeight: 32 }}>
      <div style={{ flexShrink: 0, color: "#9CA3AF", display: "flex", alignItems: "center" }}>{icon}</div>
      <span style={{ width: 110, flexShrink: 0, fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>
        {label}
      </span>
      <div style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 500, color: "#111827", fontFamily: "var(--font-family-base)" }}>
        {value}
      </div>
    </div>
  );
}

// ─── Description Section ─────────────────────────────────────────────────────

function DescriptionSection() {
  const [open, setOpen] = React.useState(true);
  return (
    <div style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8 }}>
      <div
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen(v => !v)}
      >
        <DescriptionIcon />
        <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
          Description
        </span>
        <div style={{ transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 200ms" }}>
          <ChevronUpIcon />
        </div>
      </div>
      {open && (
        <div style={{ padding: "0 16px 16px" }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 400, color: "#374151", fontFamily: "var(--font-family-base)", lineHeight: "20px" }}>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
            Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
            Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
            Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Parts Section ────────────────────────────────────────────────────────────

function PartsSection() {
  const [open, setOpen] = React.useState(true);
  const [parts, setParts] = React.useState<AssetPart[]>(MOCK_PARTS);

  return (
    <div style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px" }}>
        <div style={{
          width: 24, height: 24, borderRadius: "50%", background: "#EEF0FF",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <PartIcon />
        </div>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
          Parts
        </span>
        <button
          style={{
            display: "flex", alignItems: "center", gap: 4,
            border: "none", background: "transparent", cursor: "pointer", padding: 4,
            fontSize: 12, fontWeight: 700, color: "#0517f8", fontFamily: "var(--font-family-base)",
            letterSpacing: "-0.24px",
          }}
        >
          <PlusIcon color="#0517f8" />
          Assign
        </button>
        <div
          style={{ cursor: "pointer", flexShrink: 0, transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 200ms" }}
          onClick={() => setOpen(v => !v)}
        >
          <ChevronUpIcon />
        </div>
      </div>

      {open && (
        <div style={{ borderTop: "1px solid #E5E7EB" }}>
          {/* Column headers */}
          <div style={{ display: "flex", background: "#F9FAFB", padding: "0 16px" }}>
            <div style={{ flex: 1, padding: "8px 0" }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>Name</span>
            </div>
            <div style={{ width: 140, padding: "8px 0" }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>Article Number</span>
            </div>
            <div style={{ width: 36 }} />
          </div>
          {/* Rows */}
          {parts.map((part) => (
            <div key={part.id} style={{ display: "flex", alignItems: "center", padding: "0 16px", borderTop: "1px solid #E5E7EB" }}>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, padding: "10px 0" }}>
                <PartRowIcon />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px" }}>
                  {part.name}
                </span>
              </div>
              <div style={{ width: 140 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: "#374151", fontFamily: "var(--font-family-base)" }}>
                  {part.articleNumber}
                </span>
              </div>
              <div style={{ width: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <button
                  onClick={() => setParts(prev => prev.filter(p => p.id !== part.id))}
                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}
                >
                  <RemoveIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Procedures Section ───────────────────────────────────────────────────────

function ProceduresSection() {
  const [open, setOpen] = React.useState(true);
  const [procedures, setProcedures] = React.useState<AssetProcedure[]>(MOCK_PROCEDURES);

  return (
    <div style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px" }}>
        <div style={{
          width: 24, height: 24, borderRadius: "50%", background: "#EEF0FF",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <ProcedureIcon />
        </div>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
          Procedures
        </span>
        <button
          style={{
            display: "flex", alignItems: "center", gap: 4,
            border: "none", background: "transparent", cursor: "pointer", padding: 4,
            fontSize: 12, fontWeight: 700, color: "#0517f8", fontFamily: "var(--font-family-base)",
            letterSpacing: "-0.24px",
          }}
        >
          <PlusIcon color="#0517f8" />
          Assign
        </button>
        <div
          style={{ cursor: "pointer", flexShrink: 0, transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 200ms" }}
          onClick={() => setOpen(v => !v)}
        >
          <ChevronUpIcon />
        </div>
      </div>

      {open && (
        <div style={{ borderTop: "1px solid #E5E7EB" }}>
          {/* Column headers */}
          <div style={{ display: "flex", background: "#F9FAFB", padding: "0 16px" }}>
            <div style={{ flex: 1, padding: "8px 0" }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>Procedure Name</span>
            </div>
            <div style={{ width: 80, padding: "8px 0" }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>Fields</span>
            </div>
            <div style={{ width: 120, padding: "8px 0" }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>Created Date</span>
            </div>
            <div style={{ width: 36 }} />
          </div>
          {/* Rows */}
          {procedures.map((proc) => (
            <div key={proc.id} style={{ display: "flex", alignItems: "center", padding: "0 16px", borderTop: "1px solid #E5E7EB" }}>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, padding: "10px 0" }}>
                <ProcedureRowIcon />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px" }}>
                  {proc.name}
                </span>
              </div>
              <div style={{ width: 80 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: "#374151", fontFamily: "var(--font-family-base)" }}>
                  {proc.fields}
                </span>
              </div>
              <div style={{ width: 120 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: "#374151", fontFamily: "var(--font-family-base)" }}>
                  {proc.createdDate}
                </span>
              </div>
              <div style={{ width: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <button
                  onClick={() => setProcedures(prev => prev.filter(p => p.id !== proc.id))}
                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}
                >
                  <RemoveIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Right Sidebar ────────────────────────────────────────────────────────────

function DetailsSidebarSection() {
  const [open, setOpen] = React.useState(true);

  return (
    <div style={{ borderBottom: "1px solid #E5E7EB" }}>
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px 0", cursor: "pointer" }}
        onClick={() => setOpen(v => !v)}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
          Details
        </span>
        <div style={{ transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 200ms" }}>
          <ChevronUpIcon />
        </div>
      </div>

      {open && (
        <div style={{ padding: "8px 20px 16px" }}>
          <SidebarRow
            icon={<CalendarIcon />}
            label="Created at"
            value="12/06/2026"
          />
          <SidebarRow
            icon={<UserIcon />}
            label="Created by"
            value={
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Avatar initials="JM" color="#2563EB" />
                <span style={{ fontSize: 12, fontWeight: 500, color: "#111827", fontFamily: "var(--font-family-base)" }}>
                  James Mcdonald
                </span>
              </div>
            }
          />
          <SidebarRow
            icon={<SNIcon />}
            label="Serial Number"
            value="00000123"
          />
          <SidebarRow
            icon={<AssetTypeIcon />}
            label="Asset Type"
            value="Machine"
          />
          <SidebarRow
            icon={<TeamIcon />}
            label="Team"
            value={
              <span style={{
                display: "inline-flex", alignItems: "center",
                padding: "2px 8px", borderRadius: 4,
                background: "#F3F4F6", color: "#374151",
                fontSize: 11, fontWeight: 700, fontFamily: "var(--font-family-base)",
                letterSpacing: "0.5px",
              }}>
                TECHNICIAN
              </span>
            }
          />
        </div>
      )}
    </div>
  );
}

function CompanySidebarSection() {
  return (
    <div style={{ borderBottom: "1px solid #E5E7EB", padding: "16px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
          Company
        </span>
        <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, fontSize: 12, fontWeight: 600, color: "#0517f8", fontFamily: "var(--font-family-base)" }}>
          Assign
        </button>
      </div>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 12px",
        background: "white",
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: "#F3F4F6",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <BuildingIcon color="#748094" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.26px", lineHeight: "18px" }}>
            Acme International
          </p>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
            0000123
          </p>
        </div>
        <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex", flexShrink: 0 }}>
          <MoreDotsIcon />
        </button>
      </div>
    </div>
  );
}

function ParentAssetSidebarSection() {
  return (
    <div style={{ borderBottom: "1px solid #E5E7EB", padding: "16px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
          Parent Asset
        </span>
        <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, fontSize: 12, fontWeight: 600, color: "#0517f8", fontFamily: "var(--font-family-base)" }}>
          Assign
        </button>
      </div>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 12px",
        background: "white",
      }}>
        <AssetThumb size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.26px", lineHeight: "18px" }}>
            Acme Asset 2000
          </p>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
            00000XP
          </p>
        </div>
        <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex", flexShrink: 0 }}>
          <MoreDotsIcon />
        </button>
      </div>
    </div>
  );
}

function CustomFieldRow({ icon, label, value, isCustomBadge }: { icon: React.ReactNode; label: string; value: string; isCustomBadge?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", minHeight: 30 }}>
      <div style={{ flexShrink: 0, color: "#9CA3AF", display: "flex", alignItems: "center" }}>{icon}</div>
      <span style={{ width: 100, flexShrink: 0, fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>
        {label}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {isCustomBadge ? (
          <span style={{
            display: "inline-flex", alignItems: "center",
            padding: "2px 8px", borderRadius: 4,
            background: "#EEF0FF", color: "#0517f8",
            fontSize: 11, fontWeight: 700, fontFamily: "var(--font-family-base)",
            letterSpacing: "0.3px",
          }}>
            CUSTOM
          </span>
        ) : (
          <span style={{
            fontSize: 12, fontWeight: 500, color: "#374151",
            fontFamily: "var(--font-family-base)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            display: "block",
          }}>
            {value}
          </span>
        )}
      </div>
    </div>
  );
}

function CustomFieldsSidebarSection() {
  const [open, setOpen] = React.useState(true);

  return (
    <div style={{ padding: "16px 20px" }}>
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: open ? 8 : 0, cursor: "pointer" }}
        onClick={() => setOpen(v => !v)}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
          Custom Fields
        </span>
        <div style={{ transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 200ms" }}>
          <ChevronUpIcon />
        </div>
      </div>

      {open && (
        <div>
          <CustomFieldRow icon={<SingleSelectIcon />} label="Single Select" value="" isCustomBadge />
          <CustomFieldRow icon={<MultiSelectIcon />} label="Multi Select" value="" isCustomBadge />
          <CustomFieldRow icon={<TextIcon />} label="Text" value="Something goes here for the..." />
          <CustomFieldRow icon={<NumberIcon />} label="Number" value="Something goes here for the..." />
          <CustomFieldRow icon={<DateFieldIcon />} label="Date" value="Something goes here for the..." />
          <button style={{
            marginTop: 8, border: "none", background: "transparent", cursor: "pointer", padding: 0,
            fontSize: 12, fontWeight: 700, color: "#0517f8", fontFamily: "var(--font-family-base)",
            letterSpacing: "-0.24px",
          }}>
            Show Empty Fields
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Tab Content ──────────────────────────────────────────────────────────────

// ─── PM Events ────────────────────────────────────────────────────────────────

interface PMWorkOrder {
  id: string;
  number: string;
  title: string;
  scheduledDate: string;
  dueDate: string;
  createdDate: string;
  assignee?: string;
  assigneeInitials?: string;
  status: "Pending" | "Open" | "In Progress" | "Closed";
}

interface PMEvent {
  id: string;
  name: string;
  assetName: string;
  repeatEvery: string;
  nextDate?: string;
  lastCompletedDate?: string;
  endsOn: string;
  allOccurrencesDone?: boolean;
  autoWorkOrderTrigger?: string;
  autoWorkOrderDate?: string;
  description?: string;
  proceduresCount?: number;
  partsCount?: number;
  workOrders?: PMWorkOrder[];
}

const MOCK_PM_EVENTS: PMEvent[] = [
  {
    id: "pm1", name: "Annual Maintenance", assetName: "Super Packer 1800",
    repeatEvery: "12 months", nextDate: "23-04-2026", endsOn: "23-04-2028",
    autoWorkOrderTrigger: "1 Day before event", autoWorkOrderDate: "22-04-2026",
    description: "Full annual inspection and servicing of all mechanical, electrical, and hydraulic systems.",
    proceduresCount: 4, partsCount: 6,
    workOrders: [
      { id: "wo1", number: "#WO-2041", title: "Annual Maintenance — 2026", scheduledDate: "23-04-2026", dueDate: "30-04-2026", createdDate: "01-04-2026", assignee: "J. McDonald", assigneeInitials: "JM", status: "Open" },
      { id: "wo2", number: "#WO-2089", title: "Annual Maintenance — 2027", scheduledDate: "23-04-2027", dueDate: "30-04-2027", createdDate: "01-04-2027", status: "Pending" },
    ],
  },
  {
    id: "pm2", name: "Quarterly Maintenance", assetName: "Super Packer 1800",
    repeatEvery: "3 months", nextDate: "15-07-2025", endsOn: "Never",
    autoWorkOrderTrigger: "2 Days before event", autoWorkOrderDate: "13-07-2025",
    description: "Routine quarterly check of lubrication, filters, and safety systems.",
    proceduresCount: 2, partsCount: 3,
    workOrders: [
      { id: "wo3", number: "#WO-1931", title: "Quarterly Maintenance — Q1 2025", scheduledDate: "15-01-2025", dueDate: "22-01-2025", createdDate: "02-01-2025", assignee: "S. Chen", assigneeInitials: "SC", status: "Closed" },
      { id: "wo4", number: "#WO-2010", title: "Quarterly Maintenance — Q2 2025", scheduledDate: "15-04-2025", dueDate: "22-04-2025", createdDate: "01-04-2025", assignee: "S. Chen", assigneeInitials: "SC", status: "Closed" },
      { id: "wo5", number: "#WO-2071", title: "Quarterly Maintenance — Q3 2025", scheduledDate: "15-07-2025", dueDate: "22-07-2025", createdDate: "01-07-2025", assignee: "M. Torres", assigneeInitials: "MT", status: "In Progress" },
      { id: "wo6", number: "#WO-2102", title: "Quarterly Maintenance — Q4 2025", scheduledDate: "15-10-2025", dueDate: "22-10-2025", createdDate: "01-10-2025", status: "Pending" },
    ],
  },
  {
    id: "pm3", name: "Bi-Annual Maintenance", assetName: "Super Packer 1800",
    repeatEvery: "6 months", lastCompletedDate: "23-04-2025", endsOn: "23-04-2027",
    allOccurrencesDone: true,
    description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    proceduresCount: 3, partsCount: 3,
    workOrders: [
      { id: "wo7", number: "#WO-1744", title: "Bi-Annual Maintenance — Oct 2024", scheduledDate: "23-10-2024", dueDate: "31-10-2024", createdDate: "01-10-2024", assignee: "M. Torres", assigneeInitials: "MT", status: "Closed" },
      { id: "wo8", number: "#WO-1892", title: "Bi-Annual Maintenance — Apr 2025", scheduledDate: "23-04-2025", dueDate: "30-04-2025", createdDate: "01-04-2025", assignee: "M. Torres", assigneeInitials: "MT", status: "Closed" },
    ],
  },
];

function RepeatIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 5h10a2 2 0 0 1 2 2v1" />
      <path d="M14 11H4a2 2 0 0 1-2-2V8" />
      <path d="M10 2.5l2.5 2.5-2.5 2.5" />
      <path d="M6 13.5l-2.5-2.5 2.5-2.5" />
    </svg>
  );
}

function PMCardIcon() {
  return (
    <div style={{
      width: 24, height: 24, borderRadius: 6, flexShrink: 0,
      background: "#E0F2FE", border: "1px solid #BAE6FD",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0284C7" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="6" r="4.5" />
        <path d="M6 3.5V6l1.5 1.5" />
      </svg>
    </div>
  );
}

function CalendarSmallIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" />
      <path d="M1.5 6.5h13M5 1.5v2M11 1.5v2" />
    </svg>
  );
}

function CheckCircleSmallIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6" />
      <path d="M5 8l2 2 4-4" />
    </svg>
  );
}

function PMSearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6.5" cy="6.5" r="4.5" />
      <path d="M10.5 10.5l3 3" />
    </svg>
  );
}

function PMAssetIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1.5L2 4.5v7l6 3 6-3v-7L8 1.5z" />
      <path d="M2 4.5l6 3 6-3M8 7.5v7" />
    </svg>
  );
}

function PMWorkOrderIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="1.5" width="12" height="13" rx="1.5" />
      <path d="M5 5h6M5 7.5h6M5 10h3.5" />
      <path d="M10 9.5l1.5 1.5 2.5-2.5" />
    </svg>
  );
}

function PMDescriptionIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4h12M2 7h12M2 10h8" />
    </svg>
  );
}

function PMProcedureIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="1.5" width="12" height="13" rx="1.5" />
      <path d="M5 5h6M5 7.5h6M5 10h3" />
    </svg>
  );
}

function PMPartsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="2.5" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41" />
    </svg>
  );
}

function PMEditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#172B4D" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11 2.5z" />
    </svg>
  );
}

function PMDeleteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#EE0064" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 4.5h11M5.5 4.5V3a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5v1.5M6.5 7v5M9.5 7v5M3.5 4.5l.8 9a.5.5 0 0 0 .5.5h6.4a.5.5 0 0 0 .5-.5l.8-9" />
    </svg>
  );
}

function PMPopupLargeIcon() {
  return (
    <div style={{
      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
      background: "#E0F2FE", border: "1px solid #BAE6FD",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#0284C7" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="8" cy="8" r="6" />
        <path d="M8 5V8l2 2" />
      </svg>
    </div>
  );
}

function addMonths(dateStr: string, months: number): string {
  const [d, m, y] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1 + months, d);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${date.getFullYear()}`;
}

function generateOccurrences(event: PMEvent): string[] {
  const monthsMatch = event.repeatEvery.match(/^(\d+)/);
  if (!monthsMatch) return [];
  const interval = parseInt(monthsMatch[1]);
  const start = event.nextDate ?? event.lastCompletedDate;
  if (!start) return [];
  return Array.from({ length: 6 }, (_, i) => addMonths(start, interval * i));
}

function PMChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11L5 7l4-4" />
    </svg>
  );
}

function PMChevronRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 3l4 4-4 4" />
    </svg>
  );
}

function PMEventPopup({ event, onClose, onCreateWorkOrder }: { event: PMEvent; onClose: () => void; onCreateWorkOrder?: () => void }) {
  const [sliderIndex, setSliderIndex] = React.useState(0);
  const occurrences = generateOccurrences(event);
  const VISIBLE = 1;
  const canPrev = sliderIndex > 0;
  const canNext = sliderIndex + VISIBLE < occurrences.length;
  const rowStyle: React.CSSProperties = {
    display: "flex", alignItems: "flex-start", gap: 8,
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 500, color: "#748094",
    fontFamily: "var(--font-family-base)", lineHeight: "16px",
  };
  const valueStyle: React.CSSProperties = {
    color: "#172B4D", fontWeight: 700,
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 200 }}
      />
      {/* Modal */}
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 201, width: 380,
        background: "white", borderRadius: 8,
        boxShadow: "0px 4px 16px 0px rgba(23,43,77,0.12)",
        overflow: "hidden",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16 }}>

          {/* Title */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <PMPopupLargeIcon />
            <span style={{
              fontSize: 16, fontWeight: 700, color: "#172B4D",
              fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
            }}>
              {event.name}
            </span>
          </div>

          {/* Asset */}
          <div style={rowStyle}>
            <div style={{ marginTop: 0, flexShrink: 0 }}><PMAssetIcon /></div>
            <span style={labelStyle}>
              Asset  •  <span style={valueStyle}>{event.assetName}</span>
            </span>
          </div>

          {/* Next / Last */}
          <div style={rowStyle}>
            <div style={{ flexShrink: 0 }}><CalendarSmallIcon /></div>
            <span style={labelStyle}>
              {event.nextDate
                ? <>Next Maintenance on  •  <span style={valueStyle}>{event.nextDate}</span></>
                : <>Last completed on  •  <span style={valueStyle}>{event.lastCompletedDate}</span></>
              }
            </span>
          </div>

          {/* Repeat */}
          <div style={rowStyle}>
            <div style={{ flexShrink: 0 }}><RepeatIcon /></div>
            <span style={labelStyle}>
              Repeats  •  <span style={valueStyle}>{event.repeatEvery}</span>
            </span>
          </div>

          {/* Auto work order */}
          {event.autoWorkOrderTrigger && (
            <div style={rowStyle}>
              <div style={{ flexShrink: 0, marginTop: 1 }}><PMWorkOrderIcon /></div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={labelStyle}>
                  Automatic Work Order Creation  •  <span style={valueStyle}>{event.autoWorkOrderTrigger}</span>
                </span>
                <span style={{ ...labelStyle, color: "#748094" }}>
                  Next work order will automatically be created on {event.autoWorkOrderDate}
                </span>
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div style={rowStyle}>
              <div style={{ flexShrink: 0, marginTop: 1 }}><PMDescriptionIcon /></div>
              <span style={{ ...labelStyle, color: "#172B4D", flex: 1 }}>{event.description}</span>
            </div>
          )}

          {/* Procedures */}
          {event.proceduresCount !== undefined && (
            <div style={rowStyle}>
              <div style={{ flexShrink: 0 }}><PMProcedureIcon /></div>
              <span style={labelStyle}>
                Procedures  •  <span style={valueStyle}>{event.proceduresCount}</span>
              </span>
            </div>
          )}

          {/* Parts */}
          {event.partsCount !== undefined && (
            <div style={rowStyle}>
              <div style={{ flexShrink: 0 }}><PMPartsIcon /></div>
              <span style={labelStyle}>
                Parts  •  <span style={valueStyle}>{event.partsCount}</span>
              </span>
            </div>
          )}

          {/* Ends on */}
          <div style={rowStyle}>
            <div style={{ flexShrink: 0 }}><CalendarSmallIcon /></div>
            <span style={labelStyle}>
              Ends on  •  <span style={valueStyle}>{event.endsOn}</span>
            </span>
          </div>

          {/* Work Orders slider */}
          {occurrences.length > 0 && (
            <div style={{
              borderTop: "1px solid #E8EAED", paddingTop: 12,
              display: "flex", flexDirection: "column", gap: 10,
            }}>
              {/* Section header */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: "#A2AAB8", fontFamily: "var(--font-family-base)",
                }}>
                  Upcoming Work Orders
                </span>
              </div>

              {/* Card */}
              <div style={{ border: "1px solid #E8EAED", borderRadius: 8, padding: 10, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <PMCardIcon />
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: "#172B4D",
                        fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {event.name}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 500, color: "#748094",
                        fontFamily: "var(--font-family-base)",
                      }}>
                        {occurrences[sliderIndex]}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => { onClose(); onCreateWorkOrder?.(); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 3,
                      padding: "5px 8px", borderRadius: 6, cursor: "pointer", flexShrink: 0,
                      border: "none", background: "#0517F8",
                      fontSize: 11, fontWeight: 700, color: "white",
                      fontFamily: "var(--font-family-base)", letterSpacing: "-0.22px",
                    }}
                  >
                    <PlusIcon color="white" />
                    Create
                  </button>
                </div>
              </div>

              {/* Arrows + dots */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <button
                  onClick={() => setSliderIndex(i => i - 1)}
                  disabled={!canPrev}
                  style={{
                    width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                    border: "1px solid #E8EAED", background: "white",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: canPrev ? "pointer" : "default",
                    color: canPrev ? "#172B4D" : "#D1D5DB",
                  }}
                >
                  <PMChevronLeftIcon />
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {Array.from({ length: occurrences.length }, (_, i) => (
                    <div
                      key={i}
                      onClick={() => setSliderIndex(i)}
                      style={{
                        width: i === sliderIndex ? 14 : 5, height: 5, borderRadius: 3,
                        background: i === sliderIndex ? "#0517F8" : "#E8EAED",
                        cursor: "pointer", transition: "all 150ms ease",
                      }}
                    />
                  ))}
                </div>

                <button
                  onClick={() => setSliderIndex(i => i + 1)}
                  disabled={!canNext}
                  style={{
                    width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                    border: "1px solid #E8EAED", background: "white",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: canNext ? "pointer" : "default",
                    color: canNext ? "#172B4D" : "#D1D5DB",
                  }}
                >
                  <PMChevronRightIcon />
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4 }}>
            <button style={{
              display: "flex", alignItems: "center", gap: 4,
              padding: "8px 12px", borderRadius: 8, cursor: "pointer",
              border: "1px solid #E8EAED", background: "white",
              fontSize: 12, fontWeight: 700, color: "#172B4D",
              fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
            }}>
              <PMEditIcon /> Edit
            </button>
            <button style={{
              display: "flex", alignItems: "center", gap: 4,
              padding: "8px 12px", borderRadius: 8, cursor: "pointer",
              border: "1px solid #EE0064", background: "white",
              fontSize: 12, fontWeight: 700, color: "#EE0064",
              fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
            }}>
              <PMDeleteIcon /> Delete
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function InfoTooltip({ text }: { text: string }) {
  const [coords, setCoords] = React.useState<{ x: number; y: number } | null>(null);
  const ref = React.useRef<HTMLSpanElement>(null);

  function handleMouseEnter() {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setCoords({ x: r.left + r.width / 2, y: r.top });
    }
  }

  return (
    <span
      ref={ref}
      style={{ display: "inline-flex", alignItems: "center", marginLeft: 4 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setCoords(null)}
    >
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: "help", flexShrink: 0 }}>
        <circle cx="8" cy="8" r="7" />
        <line x1="8" y1="11" x2="8" y2="7.5" />
        <circle cx="8" cy="5.5" r="0.8" fill="#A2AAB8" stroke="none" />
      </svg>
      {coords && (
        <div style={{
          position: "fixed",
          left: coords.x,
          top: coords.y - 8,
          transform: "translate(-50%, -100%)",
          background: "#172B4D", color: "white", borderRadius: 6, padding: "6px 10px",
          fontSize: 11, fontWeight: 500, lineHeight: 1.4, whiteSpace: "normal",
          maxWidth: 160, textAlign: "center", zIndex: 9999,
          fontFamily: "var(--font-family-base)", boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
          pointerEvents: "none",
        }}>
          {text}
          <div style={{
            position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
            width: 0, height: 0,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop: "5px solid #172B4D",
          }} />
        </div>
      )}
    </span>
  );
}

function PMAccordionChevron({ open }: { open: boolean }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" fill="none"
      stroke="#748094" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      style={{ transition: "transform 200ms ease", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

function PMWOStatusBadge({ status }: { status: PMWorkOrder["status"] }) {
  const styles: Record<PMWorkOrder["status"], { bg: string; color: string }> = {
    "Pending":    { bg: "#F3F4F6", color: "#748094" },
    "Open":       { bg: "#EFF6FF", color: "#3B82F6" },
    "In Progress":{ bg: "#FFF7ED", color: "#EA580C" },
    "Closed":     { bg: "#F0FDF4", color: "#16A34A" },
  };
  const s = styles[status];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 8px", borderRadius: 20,
      background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 600, fontFamily: "var(--font-family-base)",
      whiteSpace: "nowrap",
    }}>
      {status}
    </span>
  );
}

function PMEventAccordion({ event, onCreateWorkOrder: _onCreateWorkOrder }: { event: PMEvent; onCreateWorkOrder?: () => void }) {
  const [open, setOpen] = React.useState(false);
  const [workOrders, setWorkOrders] = React.useState(event.workOrders ?? []);
  const total = workOrders.length;
  const done = workOrders.filter(wo => wo.status === "Closed").length;

  function handleCreateWO(woId: string) {
    setWorkOrders(prev => {
      const maxNum = Math.max(0, ...prev.map(w => {
        const m = w.number.match(/#WO-(\d+)/);
        return m ? parseInt(m[1]) : 0;
      }));
      return prev.map(wo =>
        wo.id === woId
          ? { ...wo, number: `#WO-${maxNum + 1}`, status: "Open" as const, createdDate: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "-") }
          : wo
      );
    });
  }

  const metaStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 5,
    fontSize: 12, fontWeight: 500, color: "#748094",
    fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
  };
  const metaValueStyle: React.CSSProperties = { color: "#172B4D", fontWeight: 700 };

  const thStyle: React.CSSProperties = {
    padding: "8px 12px", textAlign: "left",
    fontSize: 11, fontWeight: 700, color: "#A2AAB8",
    fontFamily: "var(--font-family-base)", letterSpacing: "0.04em",
    borderBottom: "1px solid #F1F3F5", whiteSpace: "nowrap",
  };
  const tdStyle: React.CSSProperties = {
    padding: "10px 12px",
    fontSize: 12, fontWeight: 500, color: "#172B4D",
    fontFamily: "var(--font-family-base)",
    borderBottom: "1px solid #F8F9FA",
    verticalAlign: "middle",
  };

  return (
    <div style={{ border: "1px solid #E8EAED", borderRadius: 8, overflow: "hidden" }}>
      {/* Accordion header */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "12px 14px", cursor: "pointer", background: "white",
          userSelect: "none",
        }}
      >
        <PMCardIcon />
        <span style={{
          fontSize: 14, fontWeight: 700, color: "#172B4D",
          fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px",
          flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {event.name}
        </span>

        {/* Meta chips */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
          <span style={metaStyle}>
            <RepeatIcon />
            Every <span style={metaValueStyle}>{event.repeatEvery}</span>
          </span>
          <span style={metaStyle}>
            <CalendarSmallIcon />
            {event.nextDate
              ? <>Next: <span style={metaValueStyle}>{event.nextDate}</span></>
              : <>Last: <span style={metaValueStyle}>{event.lastCompletedDate}</span></>
            }
          </span>
          <span style={metaStyle}>
            <CalendarSmallIcon />
            Ends: <span style={metaValueStyle}>{event.endsOn}</span>
          </span>
        </div>

        <button
          onClick={e => e.stopPropagation()}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", alignItems: "center", flexShrink: 0 }}
        >
          <MoreDotsIcon />
        </button>
        <PMAccordionChevron open={open} />
      </div>

      {/* Expanded content */}
      {open && (
        <div style={{ borderTop: "1px solid #F1F3F5" }}>
          {workOrders.length === 0 ? (
            <div style={{ padding: "20px 16px", textAlign: "center", fontSize: 12, color: "#A2AAB8", fontFamily: "var(--font-family-base)" }}>
              No work orders yet
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "25%" }} />
                <col style={{ width: "25%" }} />
                <col style={{ width: "25%" }} />
                <col style={{ width: "25%" }} />
              </colgroup>
              <thead>
                <tr style={{ background: "#FAFAFA" }}>
                  <th style={{ ...thStyle, overflow: "visible" }}>
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      TRIGGER DATE
                      <InfoTooltip text="The date the work order is automatically triggered based on the PM schedule" />
                    </span>
                  </th>
                  <th style={{ ...thStyle, overflow: "visible" }}>
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      DUE DATE
                      <InfoTooltip text="The date by which the work order must be completed" />
                    </span>
                  </th>
                  <th style={{ ...thStyle, overflow: "visible" }}>
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      CREATION DATE
                      <InfoTooltip text="The date the work order was created in the system" />
                    </span>
                  </th>
                  <th style={thStyle}>WORK ORDER</th>
                </tr>
              </thead>
              <tbody>
                {workOrders.map(wo => (
                  <tr key={wo.id}>
                    <td style={tdStyle}>{wo.scheduledDate}</td>
                    <td style={tdStyle}>{wo.dueDate}</td>
                    <td style={tdStyle}>{wo.status === "Pending" ? <span style={{ color: "#A2AAB8" }}>—</span> : wo.createdDate}</td>
                    <td style={tdStyle}>
                      {wo.status === "Pending" ? (
                        <button
                          onClick={() => handleCreateWO(wo.id)}
                          style={{
                            height: 30, padding: "0 12px",
                            border: "1.5px solid #0517F8", borderRadius: "var(--radius-s)",
                            background: "transparent", cursor: "pointer",
                            fontSize: 12, fontWeight: 700, color: "#0517F8",
                            fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Create Work Order
                        </button>
                      ) : (
                        <span style={{ color: "#0517F8", fontWeight: 700, cursor: "pointer" }}>{wo.number}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Done count */}
          <div style={{ padding: "10px 14px", borderTop: "1px solid #F1F3F5" }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
              {done} of {total} done
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Schedule view ────────────────────────────────────────────────────────────

const PM_LEFT_COL_W = 190;
const PM_CELL_W = 82;
const PM_ROW_H = 76;
const PM_HEADER_H = 48;
const MONTH_SHORT = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];

function parsePMDate(dateStr: string): Date {
  const [d, m, y] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function getPMMonthRange(events: PMEvent[]): Array<{ year: number; month: number }> {
  const all = events.flatMap(e => (e.workOrders ?? []).map(wo => parsePMDate(wo.scheduledDate)));
  if (all.length === 0) return [];
  let minY = all[0].getFullYear(), minM = all[0].getMonth();
  let maxY = all[0].getFullYear(), maxM = all[0].getMonth();
  for (const d of all) {
    if (d.getFullYear() < minY || (d.getFullYear() === minY && d.getMonth() < minM)) { minY = d.getFullYear(); minM = d.getMonth(); }
    if (d.getFullYear() > maxY || (d.getFullYear() === maxY && d.getMonth() > maxM)) { maxY = d.getFullYear(); maxM = d.getMonth(); }
  }
  let sM = minM - 1, sY = minY;
  if (sM < 0) { sM = 11; sY--; }
  let eM = maxM + 1, eY = maxY;
  if (eM > 11) { eM = 0; eY++; }
  const result: { year: number; month: number }[] = [];
  let y = sY, m = sM;
  while (y < eY || (y === eY && m <= eM)) {
    result.push({ year: y, month: m });
    m++; if (m > 11) { m = 0; y++; }
  }
  return result;
}

function PMScheduleWOPopup({ wo, onClose }: { wo: PMWorkOrder; onClose: () => void }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        zIndex: 201, width: 300, background: "white", borderRadius: 10,
        boxShadow: "0px 4px 20px rgba(23,43,77,0.14)",
        padding: 16, display: "flex", flexDirection: "column", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#3B82F6", fontFamily: "var(--font-family-base)" }}>{wo.number}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>{wo.title}</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#A2AAB8", fontSize: 14, lineHeight: 1, padding: 2, flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ height: 1, background: "#F1F3F5" }} />

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CalendarSmallIcon />
          <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
            Scheduled Date{"  •  "}<span style={{ color: "#172B4D", fontWeight: 700 }}>{wo.scheduledDate}</span>
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="5.5" r="2.5" /><path d="M2.5 13.5c0-3.038 2.462-5.5 5.5-5.5s5.5 2.462 5.5 5.5" />
          </svg>
          {wo.assignee ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{
                width: 20, height: 20, borderRadius: "50%", background: "#E0E7FF", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 8, fontWeight: 700, color: "#4338CA", fontFamily: "var(--font-family-base)",
              }}>{wo.assigneeInitials}</div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#172B4D", fontFamily: "var(--font-family-base)" }}>{wo.assignee}</span>
            </div>
          ) : (
            <span style={{ fontSize: 12, fontWeight: 500, color: "#A2AAB8", fontFamily: "var(--font-family-base)" }}>Unassigned</span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A2AAB8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="8" r="6" /><path d="M5 8l2 2 4-4" />
          </svg>
          <PMWOStatusBadge status={wo.status} />
        </div>
      </div>
    </>
  );
}

function PMScheduleView() {
  const [selectedWO, setSelectedWO] = React.useState<PMWorkOrder | null>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const months = getPMMonthRange(MOCK_PM_EVENTS);
  const today = new Date(2026, 5, 4);
  const todayMonthIdx = months.findIndex(m => m.year === today.getFullYear() && m.month === today.getMonth());
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const todayOffset = todayMonthIdx >= 0 ? todayMonthIdx * PM_CELL_W + (today.getDate() / daysInMonth) * PM_CELL_W : -1;

  React.useEffect(() => {
    if (scrollRef.current && todayOffset > 0) {
      scrollRef.current.scrollLeft = Math.max(0, todayOffset - PM_CELL_W * 4);
    }
  }, []);

  const border = "1px solid #E8EAED";

  const getWOs = (event: PMEvent, year: number, month: number) =>
    (event.workOrders ?? []).filter(wo => {
      const d = parsePMDate(wo.scheduledDate);
      return d.getFullYear() === year && d.getMonth() === month;
    });

  return (
    <div style={{ display: "flex", border, borderRadius: 8, overflow: "hidden" }}>
      {/* Fixed left column */}
      <div style={{ width: PM_LEFT_COL_W, flexShrink: 0, borderRight: border }}>
        <div style={{
          height: PM_HEADER_H, display: "flex", alignItems: "center", padding: "0 14px",
          borderBottom: border, background: "#FAFAFA",
          fontSize: 11, fontWeight: 700, color: "#A2AAB8", fontFamily: "var(--font-family-base)", letterSpacing: "0.05em",
        }}>
          EVENT TRACK
        </div>
        {MOCK_PM_EVENTS.map((event, i) => (
          <div key={event.id} style={{
            height: PM_ROW_H, display: "flex", flexDirection: "column", justifyContent: "center",
            padding: "0 14px", borderBottom: i < MOCK_PM_EVENTS.length - 1 ? border : "none", background: "white",
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.26px" }}>
              {event.name}
            </span>
            <span style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginTop: 3 }}>
              Every {event.repeatEvery}
            </span>
          </div>
        ))}
      </div>

      {/* Scrollable month columns */}
      <div ref={scrollRef} style={{ flex: 1, overflowX: "auto", overflowY: "visible", position: "relative" }}>
        <div style={{ minWidth: months.length * PM_CELL_W, position: "relative" }}>
          {/* Today line */}
          {todayOffset >= 0 && (
            <div style={{ position: "absolute", top: 0, bottom: 0, left: todayOffset, width: 2, background: "#3B82F6", zIndex: 2, pointerEvents: "none" }}>
              <div style={{
                position: "absolute", top: 6, left: "50%", transform: "translateX(-50%)",
                background: "#3B82F6", color: "white", borderRadius: 4, whiteSpace: "nowrap",
                fontSize: 10, fontWeight: 700, fontFamily: "var(--font-family-base)", padding: "2px 6px",
              }}>Today</div>
            </div>
          )}

          {/* Month headers */}
          <div style={{ display: "flex", height: PM_HEADER_H, background: "#FAFAFA", borderBottom: border }}>
            {months.map((m, i) => (
              <div key={`h-${m.year}-${m.month}`} style={{
                width: PM_CELL_W, flexShrink: 0, borderRight: border,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, color: "#A2AAB8",
                fontFamily: "var(--font-family-base)", letterSpacing: "0.04em",
              }}>
                {(i === 0 || m.month === 0)
                  ? `${MONTH_SHORT[m.month]} '${String(m.year).slice(2)}`
                  : MONTH_SHORT[m.month]}
              </div>
            ))}
          </div>

          {/* Event rows */}
          {MOCK_PM_EVENTS.map((event, ri) => (
            <div key={event.id} style={{ display: "flex", height: PM_ROW_H, borderBottom: ri < MOCK_PM_EVENTS.length - 1 ? border : "none" }}>
              {months.map((m) => {
                const wos = getWOs(event, m.year, m.month);
                return (
                  <div key={`${event.id}-${m.year}-${m.month}`} style={{
                    width: PM_CELL_W, flexShrink: 0, borderRight: border,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    gap: 4, padding: 5,
                  }}>
                    {wos.map(wo => (
                      <div
                        key={wo.id}
                        onClick={() => setSelectedWO(wo)}
                        title={wo.title}
                        style={{
                          width: "100%", padding: "4px 6px", boxSizing: "border-box",
                          background: "#F3F4F6", border: "1px solid #E2E5E8", borderRadius: 6,
                          cursor: "pointer", transition: "background 120ms ease",
                          fontSize: 10, fontWeight: 600, color: "#172B4D",
                          fontFamily: "var(--font-family-base)",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = "#E8EAED")}
                        onMouseLeave={e => (e.currentTarget.style.background = "#F3F4F6")}
                      >
                        {wo.title}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {selectedWO && <PMScheduleWOPopup wo={selectedWO} onClose={() => setSelectedWO(null)} />}
    </div>
  );
}

// ─── PM Events Tab ─────────────────────────────────────────────────────────────

function PMEventsTab({ onCreateWorkOrder }: { onCreateWorkOrder?: () => void }) {
  return (
    <div style={{
      flex: 1, padding: 24, background: "white", display: "flex", flexDirection: "column",
      overflowY: "auto",
    }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 16, flexShrink: 0 }}>
        <button style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", padding: "4px" }}>
          <PlusIcon />
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0517F8", fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px" }}>
            Create Event
          </span>
        </button>

        <button style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 40, height: 40, borderRadius: 8, border: "none",
          background: "var(--color-bg-accent, #F3F4F6)", cursor: "pointer",
        }}>
          <PMSearchIcon />
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {MOCK_PM_EVENTS.map(event => (
          <PMEventAccordion key={event.id} event={event} onCreateWorkOrder={onCreateWorkOrder} />
        ))}
      </div>

    </div>
  );
}

function ComingSoonTab({ label }: { label: string }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: 40 }}>
      <span style={{ fontSize: 32 }}>🚧</span>
      <p style={{ fontSize: 15, fontWeight: 700, color: "#111827", margin: 0, fontFamily: "var(--font-family-base)" }}>{label}</p>
      <p style={{ fontSize: 13, color: "#748094", margin: 0, fontFamily: "var(--font-family-base)" }}>This tab is coming soon.</p>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS: AssetTab[] = ["Overview", "Activity Log", "History", "Documentation", "Sub Assets", "PM Events"];

export function AssetDetailPage({ assetId: _assetId, assetName, onBack, onCreateWorkOrder }: AssetDetailPageProps) {
  const [activeTab, setActiveTab] = React.useState<AssetTab>("Overview");

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "white", overflow: "hidden" }}>

      {/* ── Page header (breadcrumb + actions) ────────────────────────────── */}
      <PageHeader
        breadcrumbs={[{ label: "Assets", onClick: onBack }, { label: assetName }]}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button style={{
              height: 40, padding: "0 14px",
              border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)",
              background: "var(--color-bg-primary)", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
              fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
            }}>
              <SparkleIcon />
              Ask AI
            </button>
            <button style={{
              width: 40, height: 40,
              border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)",
              background: "var(--color-bg-primary)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <MoreDotsIcon color="#748094" />
            </button>
            <button
              onClick={onCreateWorkOrder}
              style={{
                height: 40, padding: "0 16px",
                border: "none", borderRadius: "var(--radius-s)",
                background: "var(--color-bg-brand-dark)", cursor: "pointer",
                fontSize: 12, fontWeight: 700, color: "white",
                fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
              }}
            >
              Create Work Order
            </button>
          </div>
        }
      />

      {/* ── Two-column body ──────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* Left column: hero + tabs + content */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {/* Asset hero info */}
          <div style={{
            display: "flex", alignItems: "center", gap: 16,
            padding: "20px 24px 0",
            background: "white", flexShrink: 0,
          }}>
            <AssetThumb size={68} />

            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{
                margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: "-0.4px",
                color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", lineHeight: "28px",
              }}>
                {assetName}
              </h1>
              <p style={{ margin: "2px 0 0", fontSize: 13, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
                FL-500-0001
              </p>
            </div>

            <button style={{
              width: 40, height: 40, borderRadius: 8,
              border: "1px solid #E5E7EB",
              background: "white", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="1" y="1" width="7" height="7" rx="1" stroke="#374151" strokeWidth="1.3" />
                <rect x="12" y="1" width="7" height="7" rx="1" stroke="#374151" strokeWidth="1.3" />
                <rect x="1" y="12" width="7" height="7" rx="1" stroke="#374151" strokeWidth="1.3" />
                <rect x="2.5" y="2.5" width="4" height="4" fill="#374151" />
                <rect x="13.5" y="2.5" width="4" height="4" fill="#374151" />
                <rect x="2.5" y="13.5" width="4" height="4" fill="#374151" />
                <path d="M12 12h2.5M16 12H19M12 15h2M14.5 15h2M17.5 15h1.5M12 18h2M15 18h2M18 18h1" stroke="#374151" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Tab bar */}
          <div style={{
            display: "flex", gap: 0, flexShrink: 0,
            borderBottom: "1px solid var(--color-border-primary)",
            padding: "0 24px", marginTop: 12,
          }}>
            {TABS.map((tab) => {
              const active = tab === activeTab;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: "12px 8px 0", marginRight: 4,
                    border: "none", background: "transparent", cursor: "pointer",
                    display: "flex", flexDirection: "column", gap: 12, alignItems: "center",
                    fontFamily: "var(--font-family-base)",
                  }}
                >
                  <span style={{
                    fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
                    color: active ? "var(--color-content-primary)" : "#a2aab8",
                    whiteSpace: "nowrap",
                  }}>
                    {tab}
                  </span>
                  <div style={{
                    height: 2, width: "100%", borderRadius: "2px 2px 0 0",
                    background: active ? "#0517f8" : "transparent",
                  }} />
                </button>
              );
            })}
          </div>

          {/* Scrollable tab content */}
          {activeTab === "Overview" ? (
            <div style={{ flex: 1, overflowY: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16, background: "white" }}>
              <DescriptionSection />
              <PartsSection />
              <ProceduresSection />
            </div>
          ) : activeTab === "PM Events" ? (
            <PMEventsTab onCreateWorkOrder={onCreateWorkOrder} />
          ) : (
            <ComingSoonTab label={activeTab} />
          )}
        </div>

        {/* Right sidebar */}
        <div style={{
          width: 320, flexShrink: 0,
          borderLeft: "1px solid #E5E7EB",
          overflowY: "auto",
          background: "white",
        }}>
          <DetailsSidebarSection />
          <CompanySidebarSection />
          <ParentAssetSidebarSection />
          <CustomFieldsSidebarSection />
        </div>
      </div>
    </div>
  );
}
